import React, { useCallback, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { parse } from "date-fns";
import _ from "lodash";
import moment from "moment";
import Select from "react-select";
import PhoneInput from "react-phone-input-2";
import { Switch } from "@headlessui/react";

// constants
import "react-phone-input-2/lib/style.css";
import { checkDateFormatYMD } from "@utilscommon";
import { LABEL_POSITION_LEFT } from "@constant";
import {
  DATE_FORMAT,
  DATE_FORMAT_FNS,
  DATE_TIME_FORMAT,
  DATE_TIME_FORMAT_FNS,
  LOCAL_DATE_FORMAT,
  DISPLAY_BROWSER_DATE_FORMAT,
} from "@datehandle";
import LocaleStrings from "@language";

export const renderFieldText = (field) => {
  var { meta, labelposition, label, placeholder, input, type } = field;
  var divClassName = `space-y-1 2xs:space-y-1.5 ${
    meta.touched && meta.error ? "has-danger" : ""
  }`;
  var labelClasses = "control-label-top col-span-12";
  var inputClasses = "col-span-12";

  if (labelposition === LABEL_POSITION_LEFT) {
    labelClasses = "control-label xs:col-span-12 sm:col-span-12 md:col-span-3";
    inputClasses = "xs:col-span-12 sm:col-span-12 md:col-span-9";
  }

  return (
    <div className={divClassName}>
      {label !== "" ? (
        <label htmlFor={input?.name ?? ""} className={labelClasses}>
          {label}
          {field.mandatory === true ? (
            <span className="label-mandatory">*</span>
          ) : (
            ""
          )}
        </label>
      ) : null}

      <div className={inputClasses}>
        <input
          className="input-field common-field"
          type={type}
          placeholder={placeholder}
          {...input}
          maxLength={field?.maxlength ? field?.maxlength : ""}
          disabled={field?.disabled === true ? true : false}
          autoComplete="none"
        />
        <div className="text-help">{meta.touched ? meta.error : ""}</div>
      </div>
    </div>
  );
};

export const renderHiddenFieldText = (field) => {
  var { meta } = field;
  var divClassName = `${meta.error ? "has-danger" : ""}`;

  return (
    <div className={divClassName}>
      <div className="hidden">
        <input type={field.type} {...field.input} />
      </div>
      <div className="text-help">{meta.error ? meta.error : ""}</div>
    </div>
  );
};

export const renderFieldTextarea = (field) => {
  var { label, labelposition, meta, input } = field;
  var divClassName = `space-y-1 2xs:space-y-1.5 ${
    meta.touched && meta.error ? "has-danger" : ""
  }`;
  var labelClasses = "control-label-top col-span-12";
  var inputClasses = "col-span-12";

  if (labelposition === LABEL_POSITION_LEFT) {
    labelClasses = "control-label xs:col-span-12 sm:col-span-12 md:col-span-3";
    inputClasses = "xs:col-span-12 sm:col-span-12 md:col-span-9";
  }

  return (
    <div className={divClassName}>
      {label !== "" ? (
        <label className={labelClasses}>
          {label}
          {field.mandatory === true ? (
            <span className="label-mandatory">*</span>
          ) : (
            ""
          )}
        </label>
      ) : null}

      <div className={inputClasses}>
        <textarea
          className="textarea-field common-field"
          placeholder={field.placeholder}
          {...input}
          maxLength={field?.maxlength ? field?.maxlength : ""}
          disabled={field?.disabled === true ? true : false}
          autoComplete="none"
        ></textarea>
        <div className="text-help">{meta.touched ? meta.error : ""}</div>
      </div>
    </div>
  );
};

export const renderFieldSelect = (field) => {
  var { label, labelposition, meta, input } = field;
  var divClassName = `space-y-1 2xs:space-y-1.5 ${
    meta.touched && meta.error ? "has-danger" : ""
  }`;
  var labelClasses = "control-label-top col-span-12";
  var inputClasses = "col-span-12";

  if (labelposition === LABEL_POSITION_LEFT) {
    labelClasses = "control-label xs:col-span-12 sm:col-span-12 md:col-span-3";
    inputClasses = "xs:col-span-12 sm:col-span-12 md:col-span-9";
  }

  return (
    <div className={divClassName}>
      {label !== "" ? (
        <label className={labelClasses}>
          {label}
          {field.mandatory === true ? (
            <span className="label-mandatory">*</span>
          ) : (
            ""
          )}
        </label>
      ) : null}

      <div className={inputClasses}>
        <select
          className="input-field common-field bg-white"
          {...input}
          disabled={field?.disabled === true ? true : false}
        >
          {field?.placeholder ? (
            <option value={" "} key={field.label}>
              {field.placeholder}
            </option>
          ) : (
            ""
          )}
          {field.options.map((object, index) => {
            return (
              <option key={`${object.value}-${index}`} value={object.value}>
                {object?.label ?? object?.text}
              </option>
            );
          })}
        </select>
        <div className="text-help">{meta.touched ? meta.error : ""}</div>
      </div>
    </div>
  );
};

export const renderFieldCheckbox = (field) => {
  let { label, opts, input, labelposition, meta } = field;
  var divClassName = `space-y-1 2xs:space-y-1.5 ${
    meta.touched && meta.error ? "has-danger" : ""
  }`;
  var labelClasses = "control-label-top col-span-12";
  var inputClasses = "col-span-12";

  if (labelposition === LABEL_POSITION_LEFT) {
    labelClasses = "control-label xs:col-span-12 sm:col-span-12 md:col-span-3";
    inputClasses = "xs:col-span-12 sm:col-span-12 md:col-span-9";
  }
  return (
    <div className={divClassName}>
      {label !== "" ? (
        <label className={labelClasses}>
          {label}
          {field.mandatory === true ? (
            <span className="label-mandatory">*</span>
          ) : (
            ""
          )}
        </label>
      ) : null}

      <div className={`${inputClasses} space-y-1`}>
        {opts.map((option, index) => {
          return (
            <div key={index} className="flex items-center space-x-3">
              <div className="flex h-6 items-center">
                <input
                  className="checkbox-field"
                  type="checkbox"
                  name={`${input.name}[${index}]`}
                  value={option.value}
                  checked={input.value.indexOf(option.value) !== -1}
                  onChange={(event) => {
                    const newValue = [...input.value];
                    if (event.target.checked) {
                      newValue.push(option.value);
                    } else {
                      newValue.splice(newValue.indexOf(option.value), 1);
                    }
                    return input.onChange(newValue);
                  }}
                  onBlur={() => input.onBlur(input.value)}
                  disabled={field?.disabled === true ? true : false}
                />
              </div>

              <div
                htmlFor={field?.input?.name ?? ""}
                className="custom-text-primary-md-normal"
              >
                {option.text}
              </div>
            </div>
          );
        })}
        <div className="text-help">{meta.touched ? meta.error : ""}</div>
      </div>
    </div>
  );
};

export const renderFieldRadio = (field) => {
  let { label, opts, input, labelposition, meta } = field;
  var divClassName = `space-y-1 2xs:space-y-1.5 ${
    meta.touched && meta.error ? "has-danger" : ""
  }`;
  var labelClasses = "control-label-top col-span-12";
  var inputClasses = "col-span-12";

  if (labelposition === LABEL_POSITION_LEFT) {
    labelClasses = "control-label xs:col-span-12 sm:col-span-12 md:col-span-3";
    inputClasses = "xs:col-span-12 sm:col-span-12 md:col-span-9";
  }
  return (
    <div className={divClassName}>
      {label !== "" ? (
        <label className={labelClasses}>
          {label}
          {field.mandatory === true ? (
            <span className="label-mandatory">*</span>
          ) : (
            ""
          )}
        </label>
      ) : null}

      <div className={`${inputClasses} space-y-1`}>
        {opts.map((option, index) => {
          return (
            <div key={index} className="flex items-center space-x-3">
              <div className="flex h-6 items-center">
                <input
                  {...field.input}
                  name={`${input.name}`}
                  value={option.value}
                  className="radio-field"
                  type="radio"
                  checked={input.value === option.value}
                  disabled={field?.disabled === true ? true : false}
                />
              </div>

              <div
                htmlFor={field?.input?.name ?? ""}
                className="custom-text-primary-md-normal"
              >
                {option.text}
              </div>
            </div>
          );
        })}
        <div className="text-help">{meta.touched ? meta.error : ""}</div>
      </div>
    </div>
  );
};

export const renderSingleRadio = (field) => {
  return (
    <div className="flex items-center space-x-3">
      <div className="flex h-6 items-center">
        <input
          className="radio-field"
          {...field.input}
          type="radio"
          disabled={field?.disabled === true ? true : false}
        />
      </div>

      <div
        htmlFor={field?.input?.name ?? ""}
        className="custom-text-primary-md-normal"
      >
        {field.label}
      </div>
    </div>
  );
};

export const renderSingleCheckbox = (field) => {
  let { meta } = field;
  var divClassName = `${meta.touched && meta.error ? "has-danger" : ""}`;

  return (
    <div className={divClassName}>
      <div className="flex items-center space-x-3">
        <div className="flex h-6 items-center">
          <input
            className="checkbox-field"
            {...field.input}
            type="checkbox"
            disabled={field?.disabled === true ? true : false}
          />
        </div>

        {field?.htmllabel ? (
          <div className="flex">
            <div
              htmlFor={field?.input?.name ?? ""}
              className="custom-text-primary-md-normal"
              dangerouslySetInnerHTML={{ __html: field.htmllabel }}
            />
            {field.mandatory === true ? (
              <span className="label-mandatory">*</span>
            ) : (
              ""
            )}
          </div>
        ) : (
          <div
            htmlFor={field?.input?.name ?? ""}
            className="custom-text-primary-md-normal"
          >
            {field.label}
            {field.mandatory === true ? (
              <span className="label-mandatory">*</span>
            ) : (
              ""
            )}
          </div>
        )}
      </div>
      <div className="text-help">{meta.touched ? meta.error : ""}</div>
    </div>
  );
};

export const renderDatePicker = (field) => {
  const {
    input,
    meta,
    disabled,
    labelposition,
    placeholder,
    pickertype, // "single" or "range"
    minDate,
    maxDate,
    selected,
    label,
    mandatory,
  } = field;

  const isRange = pickertype === "range";

  // Ensure value shape
  var selectedValue = input?.value
    ? input.value
    : field?.selected
    ? field.selected
    : null;
  const normalizeDate = (date) => {
    if (!date) return null;
    if (typeof date === "string") {
      if (!checkDateFormatYMD(date)) {
        date = moment(date, DISPLAY_BROWSER_DATE_FORMAT).format(DATE_FORMAT);
      } else {
        date = moment(date).format(DATE_FORMAT);
      }

      if (!moment(date).isValid()) return "";
      return parse(date, DATE_FORMAT_FNS, new Date());
    }
    if (moment(date).isValid()) {
      return date instanceof Date ? date : new Date(date);
    }
    return null;
  };
  if (pickertype === "range") {
    if (Array.isArray(selectedValue)) {
      selectedValue = selectedValue.map(normalizeDate).filter(Boolean);
      if (selectedValue.length < 2) {
        selectedValue = [selectedValue[0] || null, null];
      }
    } else if (
      typeof selectedValue === "string" &&
      selectedValue.includes(" - ")
    ) {
      const [startStr, endStr] = selectedValue
        .split(" - ")
        .map((s) => s.trim());
      const startDate = normalizeDate(startStr);
      const endDate = normalizeDate(endStr);
      selectedValue = [startDate, endDate];
    } else {
      selectedValue = [null, null];
    }
  } else {
    selectedValue = normalizeDate(selectedValue);
  }

  if (isRange) {
    // Validate range [start, end]
    selectedValue = Array.isArray(selectedValue) ? selectedValue : [null, null];
    const [start, end] = selectedValue;

    selectedValue = [
      start && !isNaN(new Date(start)) ? new Date(start) : null,
      end && !isNaN(new Date(end)) ? new Date(end) : null,
    ];
  } else {
    // Validate single date
    selectedValue =
      selectedValue && !isNaN(new Date(selectedValue))
        ? new Date(selectedValue)
        : null;
  }
  input.value = selectedValue;

  // Layout
  const divClassName = `space-y-1 2xs:space-y-1.5 ${
    meta.touched && meta.error ? "has-danger" : ""
  }`;
  const labelClasses =
    labelposition === "left"
      ? "control-label xs:col-span-12 sm:col-span-12 md:col-span-3"
      : "control-label-top col-span-12";
  const inputClasses =
    labelposition === "left"
      ? "xs:col-span-12 sm:col-span-12 md:col-span-9"
      : "col-span-12";

  const minDateVal = minDate ? new Date(minDate) : "";
  const maxDateVal = maxDate ? new Date(maxDate) : "";
  // console.log({ minDateVal, maxDateVal });

  return (
    <div className={divClassName}>
      {label && (
        <label htmlFor={input?.name ?? ""} className={labelClasses}>
          {label}
          {mandatory && <span className="label-mandatory">*</span>}
        </label>
      )}

      <div className={inputClasses}>
        <DatePicker
          {...input}
          selectsRange={isRange}
          startDate={isRange ? selectedValue[0] : undefined}
          endDate={isRange ? selectedValue[1] : undefined}
          selected={!isRange ? selectedValue : undefined}
          onChange={(date) => {
            // Important: react-datepicker returns null or [start, end]
            if (isRange) {
              if (!date || date.length !== 2) {
                input.onChange([null, null]);
              } else {
                input.onChange(date);
              }
            } else {
              input.onChange(date);
            }
          }}
          placeholderText={placeholder ? placeholder : ""}
          dateFormat={LOCAL_DATE_FORMAT}
          className="input-field common-field"
          disabled={disabled}
          minDate={minDateVal}
          maxDate={maxDateVal}
          showMonthYearDayPicker
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          showIcon
          isClearable
        />
        <div className="text-help">{meta.touched ? meta.error : ""}</div>
      </div>
    </div>
  );
};

export const renderDateTimePicker = (field) => {
  // console.log('field :- ', field);
  const { meta, input, disabled, labelposition, placeholder } = field;
  var selectedValue = input?.value
    ? input.value
    : field?.selected
    ? field.selected
    : null;

  // console.log("Type :- ", typeof selectedValue);
  // console.log("selectedValue -----:- ", selectedValue);

  if (typeof selectedValue == "string" && selectedValue !== "") {
    let date = "";
    var regex = new RegExp(
      /[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1]) (2[0-3]|[01][0-9]):[0-5][0-9]/
    );

    // Check 'YYYY-MM-DD HH:mm' format, this format is coz we are storing the date in this format, and as per format conver string date to object.
    if (!regex.test(selectedValue)) {
      date = moment(
        selectedValue,
        `${DISPLAY_BROWSER_DATE_FORMAT} HH:mm`
      ).format(DATE_TIME_FORMAT);
    } else {
      date = moment(selectedValue).format(DATE_TIME_FORMAT);
    }
    // console.log("date", date);

    // if (!isNaN(new Date(date))) {
    if (!moment(date).isValid()) {
      selectedValue = "";
    } else {
      selectedValue = parse(date, DATE_TIME_FORMAT_FNS, new Date());
    }
  }

  input.value = selectedValue;
  // console.log("selectedValue :- ", selectedValue);
  // console.log("input :- ", input);

  var divClassName = `space-y-1 2xs:space-y-1.5 ${
    meta.touched && meta.error ? "has-danger" : ""
  }`;
  var labelClasses = "control-label-top col-span-12";
  var inputClasses = "col-span-12";

  if (labelposition === LABEL_POSITION_LEFT) {
    labelClasses = "control-label xs:col-span-12 sm:col-span-12 md:col-span-3";
    inputClasses = "xs:col-span-12 sm:col-span-12 md:col-span-9";
  }

  return (
    <div className={divClassName}>
      {field?.label !== "" ? (
        <label htmlFor={field?.input?.name ?? ""} className={labelClasses}>
          {field?.label}
          {field.mandatory === true ? (
            <span className="label-mandatory">*</span>
          ) : (
            ""
          )}
        </label>
      ) : null}

      <div className={inputClasses}>
        <DatePicker
          {...input}
          selected={selectedValue}
          className="input-field common-field"
          showIcon
          // isClearable
          placeholderText={placeholder ? placeholder : ""}
          minDate={field.minDate ? field.minDate : ""}
          showTimeSelect
          timeIntervals={1}
          timeFormat={`HH:mm`}
          dateFormat={`${LOCAL_DATE_FORMAT} HH:mm`}
          disabled={disabled}
          onChange={input.onChange}
        />
        <div className="text-help label-text-help">
          {meta.touched ? meta.error : ""}
        </div>
      </div>
    </div>
  );
};

// export const renderFieldPhone = (field) => {
//   let { meta, labelposition, label } = field;
//   let divClassName = `space-y-1 2xs:space-y-1.5 ${
//     meta.touched && meta.error ? "has-danger" : ""
//   }`;
//   let labelClasses = "control-label-top col-span-12";
//   let inputClasses = "col-span-12";

//   if (labelposition === LABEL_POSITION_LEFT) {
//     labelClasses = "control-label xs:col-span-12 sm:col-span-12 md:col-span-3";
//     inputClasses = "xs:col-span-12 sm:col-span-12 md:col-span-9";
//   }

//   const handlePhoneNumberInput = (v, d, e, fv) => {
//     const rawPhoneNumber =
//       !_.isEmpty(v) && !_.isEmpty(d?.dialCode)
//         ? _.slice(v, d?.dialCode?.length).join("")
//         : "";
//     const returnValue = `+${d?.dialCode}${
//       !_.isEmpty(rawPhoneNumber) ? ` ${rawPhoneNumber}` : ""
//     }`;

//     field?.input?.onChange(returnValue);
//   };

//   return (
//     <div className={divClassName}>
//       {label !== "" ? (
//         <label htmlFor={field?.input?.name ?? ""} className={labelClasses}>
//           {label}
//           {field.mandatory === true ? (
//             <span className="label-mandatory">*</span>
//           ) : (
//             ""
//           )}
//         </label>
//       ) : null}

//       <div className={inputClasses}>
//         <PhoneInput
//           inputClass="common-field"
//           name={field?.input?.name}
//           value={
//             _.isEmpty(field?.countrycode?.toString())
//               ? !_.isEmpty(field?.input?.value)
//                 ? !_.isString(field?.input?.value)
//                   ? field?.input?.value?.toString()
//                   : field?.input?.value
//                 : ""
//               : field?.countrycode.toString()
//           }
//           inputProps={{
//             autoComplete: "none",
//           }}
//           placeholder=""
//           country={
//             field?.eventid === 1797212565294160
//               ? "au"
//               : ["1", "+1"].includes(field?.countrycode?.toString())
//               ? "us"
//               : ""
//           }
//           enableSearch={true}
//           disableDropdown={
//             !!field?.isrestricted &&
//             !["1", "+1"].includes(field?.countrycode?.toString())
//           }
//           countryCodeEditable={false}
//           disabled={field?.disabled === true ? true : false}
//           onChange={(v, d, e, fv) => handlePhoneNumberInput(v, d, e, fv)}
//           onlyCountries={
//             field?.isrestricted &&
//             ["1", "+1"].includes(field?.countrycode?.toString())
//               ? ["ca", "us"]
//               : []
//           }
//         />
//         <div className="text-help">{meta.touched ? meta.error : ""}</div>
//       </div>
//     </div>
//   );
// };

export const RenderFieldPhone = (field) => {
  const { meta, labelposition, label, countryList } = field;
  const hasError = meta.touched && meta.error;
  const divClassName = `space-y-1 2xs:space-y-1.5 ${
    hasError ? "has-danger" : ""
  }`;
  const labelClasses =
    labelposition === LABEL_POSITION_LEFT
      ? "control-label xs:col-span-12 sm:col-span-12 md:col-span-3"
      : "control-label-top col-span-12";
  const inputClasses =
    labelposition === LABEL_POSITION_LEFT
      ? "xs:col-span-12 sm:col-span-12 md:col-span-9"
      : "col-span-12";

  let inputVal = field?.input?.value?.toString() || "";
  let inputValue = _.cloneDeep(inputVal);
  // console.log({ inputValue });

  const match = inputValue.match(/^(\+\d+)/);
  const countryCode = match ? match[1] : "";
  field.input.value = inputValue.replace(countryCode, "").trim();

  let defaultDialCode = "";
  if (countryCode !== "") {
    defaultDialCode = countryCode;
  }
  let rawCode = field?.countrycode?.toString() || "";
  let normalizedCode = "";
  if (rawCode !== "") {
    normalizedCode = rawCode.startsWith("+") ? rawCode : `+${rawCode}`;
  } else {
    normalizedCode = defaultDialCode;
  }

  const [selectedCountry, setSelectedCountry] = useState(() => {
    let match =
      normalizedCode !== ""
        ? countryList.find((c) => c.dialcode === normalizedCode)
        : null;

    return (
      match || countryList.find((c) => c.dialcode === defaultDialCode) || {}
    );
  });
  // console.log({ selectedCountry });

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const isDisabled = field?.isrestricted;

  useEffect(() => {
    const fullValue = inputValue;
    const raw = selectedCountry?.dialcode
      ? fullValue.replace(selectedCountry.dialcode, "").trim()
      : "";
    setPhoneNumber(raw);
  }, [inputValue, selectedCountry]);

  const handleSelect = (country) => {
    setSelectedCountry(country);
    setDropdownOpen(false);
    setSearchQuery("");

    const returnValue = `${country.dialcode}${
      phoneNumber ? ` ${phoneNumber}` : ""
    }`;
    field?.input?.onChange(returnValue);
  };

  const handleInputChange = (e) => {
    let input = e.target.value;
    let code = selectedCountry?.dialcode ?? "";

    // Keep only digits
    const newPhone = input.replace(/\D/g, "");
    setPhoneNumber(newPhone);
    let returnValue = "";
    if (newPhone !== "") {
      returnValue = `${code}${newPhone ? ` ${newPhone}` : ""}`;
    }
    field?.input?.onChange(returnValue);
  };

  const handleInputBlur = () => {
    let code = selectedCountry?.dialcode ?? "";

    // Keep only digits
    const newPhone = phoneNumber.replace(/\D/g, "");
    let returnValue = "";
    if (newPhone !== "") {
      returnValue = `${code}${newPhone ? ` ${newPhone}` : ""}`;
    }
    field.input.onBlur(returnValue);
  };

  const filteredCountries = countryList.filter((country) =>
    `${country.country} ${country.dialcode}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  return (
    <div className={divClassName}>
      {label && (
        <label htmlFor={field?.input?.name ?? ""} className={labelClasses}>
          {label}
          {field?.mandatory ? <span className="label-mandatory">*</span> : null}
        </label>
      )}

      <div className={inputClasses}>
        <div className="relative flex w-full gap-2">
          {/* Dial code button */}
          <div className="relative">
            {!dropdownOpen ? (
              <button
                type="button"
                onClick={() => !isDisabled && setDropdownOpen(!dropdownOpen)}
                disabled={isDisabled}
                className={`h-[42px] w-[80px] px-3 border border-default rounded-md bg-white whitespace-nowrap ${
                  selectedCountry?.dialcode
                    ? "custom-text-primary-md-normal"
                    : "custom-text-secondary-md-normal"
                } ${
                  isDisabled
                    ? "cursor-not-allowed bg-gray-100"
                    : "cursor-pointer"
                }`}
              >
                {selectedCountry?.dialcode ?? LocaleStrings.kCountryText}
              </button>
            ) : (
              <>
                <input
                  type="text"
                  autoFocus
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onBlur={() => setTimeout(() => setDropdownOpen(false), 200)} // Allow selection before closing
                  placeholder={LocaleStrings.kCountryText}
                  className="w-[80px] rounded-md text-base font-normal text-primary placeholder-gray-500 border border-default hover:border-theme focus:ring-theme focus:border-theme px-3 py-2 focus:outline-none"
                />
                <ul className="absolute z-50 mt-1 w-64 max-h-52 overflow-y-auto border border-gray-300 bg-white rounded-md shadow-md">
                  {filteredCountries.map((country) => (
                    <li
                      key={country.code}
                      onPointerDown={() => handleSelect(country)}
                      className="flex items-center gap-2 px-3 py-2 text-base text-gray-700 hover:bg-gray-100 cursor-pointer truncate"
                      title={`${country.country} (${country.dialcode})`}
                    >
                      <span>{country.flagemoji}</span>
                      <span className="flex-1 truncate">{country.country}</span>
                      <span className="text-gray-500">{country.dialcode}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          {/* Phone input */}
          <input
            type="tel"
            name={field?.input?.name}
            placeholder={LocaleStrings.kEnterNumberText}
            value={phoneNumber}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            // onBlur={field.input.onBlur}
            maxLength={field?.maxlength ? field?.maxlength : ""}
            // disabled={isDisabled}
            className={`input-field common-field flex-1`}
            autoComplete="none"
          />
        </div>

        {hasError && <div className="text-help">{meta.error}</div>}
      </div>
    </div>
  );
};

export const renderAutoSelect = (field) => {
  var { input, label, labelposition, meta } = field;
  var divClassName = `space-y-1 2xs:space-y-1.5 ${
    field.meta.touched && field.meta.error ? "has-danger" : ""
  }`;
  divClassName = `${divClassName} ${field.bsClass}`;

  var labelClasses = "control-label-top col-span-12";
  var inputClasses = "col-span-12";

  if (labelposition === LABEL_POSITION_LEFT) {
    labelClasses = "control-label xs:col-span-12 sm:col-span-12 md:col-span-3";
    inputClasses = "xs:col-span-12 sm:col-span-12 md:col-span-9";
  }

  let allinputval = input.value;
  let selectedArrObj = [];
  if (
    typeof allinputval !== "object" &&
    allinputval !== "" &&
    _.size(field.options) > 0
  ) {
    var nameArr = allinputval.toString().split(",");
    // console.log("nameArr :- ", nameArr);

    _.forEach(nameArr, function (values) {
      let filterValues = _.filter(field.options, (o) => {
        return o.value == values;
      });

      if (filterValues?.length > 0) {
        selectedArrObj.push(filterValues[0]);
      }
    });
    // console.log("selectedArrObj :- ", selectedArrObj);

    if (_.size(selectedArrObj) > 0) {
      field.input.value = selectedArrObj;
    }
  }

  return (
    <div className={divClassName}>
      {label !== "" ? (
        <label htmlFor={field?.input?.name ?? ""} className={labelClasses}>
          {label}
          {field.mandatory === true ? (
            <span className="label-mandatory">*</span>
          ) : (
            ""
          )}
        </label>
      ) : null}

      <div className={inputClasses}>
        <Select
          className="react-select-input-box common-field"
          {...input}
          placeholder={field?.placeholder}
          isMulti={field?.isMulti ? true : false}
          isClearable={true}
          options={field.options}
          // onBlur={() => field.input.onBlur(field.input.value)}
          isDisabled={field?.disabled === true ? true : false}
          onBlur={(event) => {
            event.preventDefault();
          }}
        />
        <div className="text-help">{meta.touched ? meta.error : ""}</div>
      </div>
    </div>
  );
};

export const renderSpinner = (fast = false) => {
  return (
    <svg
      className={`cust-btn-spinner${fast ? "-fast" : ""}`}
      viewBox="0 0 1024 1024"
      focusable="false"
      data-icon="btnloading"
      width="1em"
      height="1em"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M988 548c-19.9 0-36-16.1-36-36 0-59.4-11.6-117-34.6-171.3a440.45 440.45 0 00-94.3-139.9 437.71 437.71 0 00-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.3C772.3 66 827 103 874 150c47 47 83.9 101.8 109.7 162.7 26.7 63.1 40.2 130.2 40.2 199.3.1 19.9-16 36-35.9 36z"></path>
    </svg>
  );
};

export const ToggleSlider = ({ enabled = false, setEnabled = () => {} }) => {
  return (
    <Switch
      checked={enabled}
      onChange={setEnabled}
      className="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
    >
      <span className="sr-only">Use setting</span>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute h-full w-full rounded-md bg-white"
      />
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute mx-auto h-4 w-9 rounded-full  transition-colors duration-200 ease-in-out ${
          enabled ? "bg-theme" : "bg-gray-200"
        }`}
      />
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out${
          enabled ? " translate-x-5" : ""
        }`}
      />
    </Switch>
  );
};

// Usage example
